import {
  Button, Card, CardContent, Grid, Stack,
} from '@mui/material';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import BlockData from '../../../../models/BlockData';
import LinkComponent from '../../../Link/LinkComponent';
import { URLOpenType } from '../../../../models/URLOpenType';
import LinkData from '../../../../models/LinkData';
import ModalComponent from '../../../ModalComponent';
import BlockSettings, { BlockJustify, BlockSeparator } from '../../../../models/BlockSettings';
import BlockSeparatorPicker from '../../../Modals/BlockSettingsModal/components/BlockSeparatorPicker';
import BlockJustifyPicker from '../../../Modals/BlockSettingsModal/components/BlockJustifyPicker';
import { FileContext } from '../../../../contexts/FileContext';

export type HeaderMenuSettingsModalCloseHandler = (newItems?: LinkData[]) => void;

export interface HeaderMenuSettingsModalProps {
  menuItems: LinkData[],
  settings: BlockSettings;
  open: boolean,
  blockData: BlockData[],
  closeHandler: HeaderMenuSettingsModalCloseHandler,
  updateHandler: (item: any) => void,
  hasJustifyOptions: boolean,
  hasExtraJustifyments?: boolean,
}

function HeaderMenuSettingsModal({
  menuItems,
  open,
  closeHandler,
  updateHandler,
  blockData,
  settings,
  hasJustifyOptions,
  hasExtraJustifyments = true,
}: HeaderMenuSettingsModalProps) {
  const [error, setError] = useState('');
  const [firstLoad, setFirstLoad] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('itemsTab');
  const [headerSettings, setHeaderSettings] = useState(settings);
  const [direction, setDirection] = useState<'row' | 'column'>('row');
  const [managedItems, setManagedItems] = useState<LinkData[]>(JSON.parse(JSON.stringify(menuItems)));
  const itemKeysRef = useRef(new WeakMap<LinkData, string>());
  const getItemKey = (link: LinkData) => {
    if (!itemKeysRef.current.has(link)) {
      itemKeysRef.current.set(link, uuid());
    }
    return itemKeysRef.current.get(link) as string;
  };
  const { getDownloaddableFileUrl, fetch } = useContext(FileContext);

  useEffect(() => {
    setHeaderSettings({ ...headerSettings });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, settings?.justify, settings?.separator, settings?.align]);

  const handleClose = () => {
    setError('');
    setFirstLoad(true);
    closeHandler(null);
  };

  const handleSave = () => {
    if (!managedItems.find((item) => item.title === '')) {
      setError('');
      setFirstLoad(true);
      updateHandler(headerSettings);
      closeHandler(managedItems);
    } else {
      setError('Please enter a title');
    }
  };

  const handleAdd = () => {
    setManagedItems([...managedItems, LinkData.getDefault('New')]);
  };

  const handleChangeSeparator = (value: BlockSeparator) => {
    setHeaderSettings({ ...headerSettings, separator: value });
  };

  const handleChangeJustify = (value: BlockJustify) => {
    setHeaderSettings({ ...headerSettings, justify: value });
  };

  // PDEV3-34【2】: re-seed all local edits from props on (re)open so changes
  // discarded via Close don't carry over to the next open.
  useEffect(() => {
    if (open) {
      setHeaderSettings(settings);
      setManagedItems(JSON.parse(JSON.stringify(menuItems)));
      setActiveTab('itemsTab');
      setError('');
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    fetch();
  }, []);

  const items = useMemo(() => {
    if (managedItems.length > 0) {
      return managedItems.map((link, index) => (
        <div style={{ position: 'relative' }} key={getItemKey(link)}>
          <Card style={{ overflow: 'visible' }}>
            <CardContent>
              <LinkComponent
                link={link}
                firstLoad={firstLoad}
                blockData={blockData}
                onRemoveBlock={() => setManagedItems(managedItems.filter((item) => item !== link))}
                onChangeURLTitle={(newTitle) => { managedItems[index].title = newTitle; }}
                onChangeTargetType={(newTargetType) => {
                  managedItems[index].linkData.targetType = newTargetType;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onSelectBlock={(selected) => {
                  managedItems[index].linkData.url = selected;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onSelectUrl={(selected) => {
                  managedItems[index].linkData.url = selected;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onChangeLink={(newLink) => { managedItems[index].linkData.url = newLink; }}
                onChangeOpenType={(checked) => {
                  managedItems[index].linkData.openType = checked ? URLOpenType.NEW_TAB : URLOpenType.SAME_PAGE;
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
                onChangeFile={(file) => {
                  managedItems[index].linkData.url = getDownloaddableFileUrl(file?.file_name);
                  setManagedItems([...managedItems]);
                  setFirstLoad(false);
                }}
              />
            </CardContent>
          </Card>
        </div>
      ));
    }

    return [];
  }, [blockData, firstLoad, getDownloaddableFileUrl, managedItems]);

  useEffect(() => {
    if (!open) {
      setManagedItems(JSON.parse(JSON.stringify(menuItems)));
    }
  }, [menuItems, open]);

  return (
    <ModalComponent
      errorMessage={error}
      size="xl"
      title="Menu Settings"
      confirmButtonText="Apply"
      onConfirm={handleSave}
      show={open}
      closeModal={handleClose}
      centered
      animation={false}
      scrollable
    >
      <nav className="nav nav-tabs mb-3">
        <a
          className={`nav-link ${activeTab === 'itemsTab' ? 'active' : ''}`}
          href="#"
          onClick={() => setActiveTab('itemsTab')}
        >
          Menu List
        </a>
        <a
          className={`nav-link ${activeTab === 'settingsTab' ? 'active' : ''}`}
          href="#"
          onClick={() => setActiveTab('settingsTab')}
        >
          Style
        </a>
      </nav>
      <div className="tab-content">
        <div
          className={`tab-pane ${activeTab === 'itemsTab' ? 'active show' : ''}`}
        >
          <Stack spacing={4}>
            {items}
            <Stack key="add" spacing={0} sx={{ border: '1px dashed lightGray', p: 0 }}>
              <Button color="primary" onClick={handleAdd}>
                <AddCircleRoundedIcon />
              </Button>
            </Stack>
          </Stack>
        </div>
        <div
          className={`tab-pane ${activeTab === 'settingsTab' ? 'active show' : ''}`}
        >
          {/* MenuPositioningSettings Component  */}
          <Stack spacing={4}>
            <div>
              <Grid
                container
                direction="column"
                alignItems="start"
                justifyContent="center"
                sx={{
                  p: 2,
                  border: '1px dashed lightGray',
                  position: 'relative',
                }}
              >
                {hasJustifyOptions && (
                  <Grid item sx={{ my: 1 }}>
                    <BlockJustifyPicker
                      hasExtraJustifyments={hasExtraJustifyments}
                      value={headerSettings?.justify}
                      direction={direction}
                      onChangeJustify={(value) => {
                        handleChangeJustify(value);
                      }}
                    />
                  </Grid>
                )}

                <Grid item sx={{ my: 1 }}>
                  <BlockSeparatorPicker
                    value={headerSettings?.separator}
                    color={headerSettings?.separatorColor}
                    onChangeSeparator={(value) => {
                      handleChangeSeparator(value);
                    }}
                    onChangeSeparatorColor={(color) => {
                      setHeaderSettings({ ...headerSettings, separatorColor: color });
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Stack>
        </div>
      </div>
    </ModalComponent>
  );
}

export default HeaderMenuSettingsModal;
